import React from "react";
import { Search } from "lucide-react";

// Matches SunbedSessionLogApiItem from service
export interface SunbedLog {
  id: string; // Not in API directly, populated during map? API doesn't have ID, might need index or add ID to API response.
  // Actually, the user's PHP code shows $data doesn't include 'id'. Frontend map will likely use index.
  customer: string;
  sunbedDetails: string; // sunbed_details
  actualPackageMins: string; // actual_package_mins
  sessionDuration: string; // HTML string from session_duration
  createdBy: string; // created_by
  status: {
    label: string;
    badge_class: string;
  };
}

interface SunbedSessionLogsTableProps {
  logs: SunbedLog[];
  isLoading: boolean;
  indexOfFirstItem: number;
}

export const SunbedSessionLogsTable: React.FC<SunbedSessionLogsTableProps> = ({
  logs,
  isLoading,
  indexOfFirstItem,
}) => {
  return (
    <div className="flex-1 overflow-y-auto relative">
      <table className="w-full">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100">
          <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
            <th className="col-span-1 text-left font-bold">#</th>
            <th className="col-span-2 text-left font-bold">Customer</th>
            <th className="col-span-3 text-left font-bold">Sunbed Details</th>
            <th className="col-span-2 text-left font-bold">
              Actual Package Mins
            </th>
            <th className="col-span-2 text-left font-bold">Session Duration</th>
            <th className="col-span-1 text-left font-bold">Created By</th>
            <th className="col-span-1 text-center font-bold">Status</th>
          </tr>
        </thead>
        <tbody className="w-full divide-y divide-gray-100 bg-white">
          {isLoading ? (
            <tr className="h-64 w-full">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : logs.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full">
              <td className="w-full h-full flex flex-col items-center justify-center">
                <Search className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-lg font-medium text-gray-500">
                  No logs found
                </p>
                <p className="text-sm">Try adjusting your search terms</p>
              </td>
            </tr>
          ) : (
            <>
              {logs.map((log, idx) => (
                <tr
                  key={idx} // Using index as ID is missing in API response sample
                  className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-purple-50/30 transition-colors bg-white group text-sm w-full"
                >
                  <td className="col-span-1 text-gray-500 font-medium">
                    {indexOfFirstItem + idx + 1}
                  </td>
                  <td className="col-span-2 font-semibold text-gray-800 group-hover:text-[#885ABB] transition-colors">
                    {log.customer}
                  </td>
                  <td className="col-span-3 text-gray-600 font-medium">
                    {log.sunbedDetails}
                  </td>
                  <td className="col-span-2 text-gray-600">
                    {log.actualPackageMins}
                  </td>
                  <td className="col-span-2 text-xs text-gray-600 space-y-1">
                    <div
                      dangerouslySetInnerHTML={{ __html: log.sessionDuration }}
                    />
                  </td>
                  <td className="col-span-1 text-gray-600">{log.createdBy}</td>
                  <td className="col-span-1 text-center">
                    <span
                      className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${log.status.badge_class === "badge-success"
                          ? "bg-[#E8F5E9] text-[#2E7D32] border border-[#A5D6A7]"
                          : log.status.badge_class === "badge-primary"
                            ? "bg-[#E3F2FD] text-[#1565C0] border border-[#90CAF9]"
                            : "bg-gray-100 text-gray-800 border border-gray-200"
                        }`}
                    >
                      {log.status.label}
                    </span>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
